import { useCallback, useSyncExternalStore } from 'react';
import { formatDateTime, type DateTimeOptions, type TimeZonePref } from './format';

/** Timestamps show UTC unless the user picks local time (spec §6.7). Analytics ignore this: always UTC. */
export const TIME_ZONE_STORAGE_KEY = 'rgs.tz';

const listeners = new Set<() => void>();

export function readTimeZonePref(): TimeZonePref {
  try {
    return globalThis.localStorage?.getItem(TIME_ZONE_STORAGE_KEY) === 'local' ? 'local' : 'utc';
  } catch {
    return 'utc';
  }
}

export function setTimeZonePref(tz: TimeZonePref) {
  try {
    localStorage.setItem(TIME_ZONE_STORAGE_KEY, tz);
  } catch {
    // Blocked storage: nothing to persist, and nothing changed for the listeners either.
    return;
  }
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key === TIME_ZONE_STORAGE_KEY || e.key === null) onChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function useTimeZonePref(): [TimeZonePref, (tz: TimeZonePref) => void] {
  const tz = useSyncExternalStore(subscribe, readTimeZonePref, () => 'utc' as const);
  return [tz, setTimeZonePref];
}

/** `formatDateTime` bound to the user's zone preference. */
export function useFormatDateTime() {
  const [tz] = useTimeZonePref();
  return useCallback(
    (value: Parameters<typeof formatDateTime>[0], options: Omit<DateTimeOptions, 'tz'> = {}) =>
      formatDateTime(value, { ...options, tz }),
    [tz],
  );
}
